import { useLazyQuery, useMutation } from "@apollo/client";
import { answerSheetState } from "atoms/answerSheet";
import {
  currentProblemState,
  currentStepState,
  stepTypeState,
  totalStepState,
} from "atoms/controller";
import { problemState } from "atoms/problems";
import { quizState } from "atoms/quiz";
import { SET_ANSWER } from "graphql/sendAnswer";
import { StepTypes } from "models/controller";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useChain, useSpring, animated } from "react-spring";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import QuizBody from "./QuizBody";
import QuizFooter from "./QuizFooter";
import QuizHeader from "./QuizHeader";
import { toNumber } from "lodash";
import { getCurrentContent } from "selectors/controller";
import { userQuizState } from "atoms/userQuiz";
import { END_QUIZ, GET_QUIZ_WITHOUT_RESULT } from "graphql/quiz";
import PageLoader from "components/Layout/PageLoader";
import { getTotalSteps } from "utils/functions";
import { answerParser } from "utils/answerParser";
import { toast } from "react-toastify";

const QuizManager: React.FC = () => {
  const [quiz, setQuiz] = useRecoilState(quizState);
  const [userQuiz, setUserQuiz] = useRecoilState(userQuizState);
  const problemCount: number = useRecoilValue(problemState).length;
  const setStep = useSetRecoilState(currentStepState);
  const [answerSheet, setAnswerSheet] = useRecoilState(answerSheetState);
  const [stepType, setStepType] = useRecoilState(stepTypeState);
  const [currentProblem, setCurrentProblem] = useRecoilState(
    currentProblemState
  );
  const content = useRecoilValue(getCurrentContent);
  const [answer, setAnswer] = useState<number | undefined>(content.userAnswer);
  const [isAnswerLocked, setIsAnswerLocked] = useState(false);

  const { push } = useHistory();
  const { id } = useParams<any>();
  const setProblems = useSetRecoilState(problemState);
  const setProblem = useSetRecoilState(currentProblemState);
  const setTotalSteps = useSetRecoilState(totalStepState);

  const getQueryStringValue = (key: string) => {
    return decodeURIComponent(
      window.location.search.replace(
        new RegExp(
          "^(?:.*[&\\?]" +
            encodeURIComponent(key).replace(/[*]/g, "\\$&") +
            "(?:\\=([^&]*))?)?.*$",
          "i"
        ),
        "$1"
      )
    );
  };

  const qt = getQueryStringValue("qt");
  const token = userQuiz?.token || qt;
  const finalHeader =
    token === "null"
      ? {}
      : {
          "access-token": token,
        };

  //Graphql queries

  const [sendAnswer, { loading: answerLoading }] = useMutation(SET_ANSWER, {
    context: {
      headers: finalHeader,
    },
    notifyOnNetworkStatusChange: true,
    onError: (error) => toast.error(error.message),
    onCompleted: (data) => {
      incrementStep();
      setAnswerSheet((oldAS) => ({ ...oldAS, [content.id]: answer as number }));
      setStepType(StepTypes.ANSWER);
    },
  });

  const [getQuiz, { loading: quizLoading, error: quizError }] = useLazyQuery(
    GET_QUIZ_WITHOUT_RESULT,
    {
      context: {
        headers: finalHeader,
      },
      variables: {
        id: toNumber(id),
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const userNewQuiz = data.QUIZ_quiz;
        const newQuiz = userNewQuiz.schema;
        setStep(0);
        setProblem(-1);
        setStepType(StepTypes.INTRO);
        setQuiz(newQuiz);
        setTotalSteps(getTotalSteps(newQuiz.problems.length));
        setProblems(newQuiz.problems);
        setAnswerSheet(answerParser(JSON.parse(userNewQuiz.answers)));
        setUserQuiz({
          id: userNewQuiz.id,
          score: userNewQuiz.score,
          token: userNewQuiz.token,
          result: userNewQuiz.result,
        });
      },
    }
  );

  const [endQuiz, { loading: endLoading }] = useMutation(END_QUIZ, {
    context: {
      headers: finalHeader,
    },
    notifyOnNetworkStatusChange: true,
    onError: (error) => toast.error(error.message),
    onCompleted: (data) => {
      const uq = data.end_quiz;
      incrementStep();
      setUserQuiz({
        ...userQuiz,
        id: uq.id,
        score: uq.score,
        result: uq.result,
      });
      setStepType(StepTypes.RESULT);
    },
  });

  const handleAnswerChange = (selectedIndex: number) => {
    !isAnswerLocked && setAnswer(selectedIndex);
  };

  const incrementStep = () => setStep((currStep) => currStep + 1);
  const decrementStep = () => setStep((currStep) => currStep - 1);

  const incrementProblem = () =>
    setCurrentProblem((currProblem) => currProblem + 1);
  const decrementProblem = () =>
    setCurrentProblem((currProblem) => currProblem - 1);

  const startQuiz = () => {
    incrementStep();
    setProblem(0);
    setStepType(StepTypes.PROBLEM);
  };

  const submitAnswer = () => {
    if (isAnswerLocked) {
      incrementStep();
      setStepType(StepTypes.ANSWER);
      return;
    }
    if (currentProblem !== undefined && answer !== undefined) {
      sendAnswer({
        variables: {
          quiz_id: toNumber(userQuiz?.id),
          problem: toNumber(content.id),
          options: [toNumber(answer)],
        },
      });
    }
  };

  const nextProblem = () => {
    incrementStep();
    incrementProblem();
    setAnswer(undefined);
    setStepType(StepTypes.PROBLEM);
  };

  const showResult = () => {
    endQuiz({
      variables: {
        quiz_id: toNumber(userQuiz?.id),
      },
    });
  };

  const previousAnswer = () => {
    decrementStep();
    decrementProblem();
    setStepType(StepTypes.ANSWER);
  };

  const previousProblem = () => {
    decrementStep();
    setStepType(StepTypes.PROBLEM);
  };

  const returnToIntro = () => {
    decrementStep();
    setStepType(StepTypes.INTRO);
  };

  const returnToHome = () => {
    setStepType(StepTypes.INTRO);
    setStep(0);
    setCurrentProblem(0);
    push("/");
  };

  const nextStep = () => {
    switch (stepType) {
      case StepTypes.INTRO:
        return startQuiz;
      case StepTypes.PROBLEM:
        return submitAnswer;
      case StepTypes.ANSWER:
        return currentProblem === problemCount - 1 ? showResult : nextProblem;
      default:
        return returnToHome;
    }
  };

  const previousStep = () => {
    switch (stepType) {
      case StepTypes.INTRO:
        return returnToHome;
      case StepTypes.PROBLEM:
        return currentProblem === 0 ? returnToIntro : previousAnswer;
      case StepTypes.ANSWER:
        return previousProblem;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (answerSheet[content.id] > -1) {
      setIsAnswerLocked(true);
    } else {
      setIsAnswerLocked(false);
    }
    setAnswer(answerSheet[currentProblem]);
  }, [currentProblem, answerSheet, content.id]);

  //Animations
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  const headerAnimation = useSpring({
    ref: headerRef,
    transform: "translate(0,0)",
    from: { transform: "translate(100%,0)" },
  });

  const bodyAnimation = useSpring({
    ref: bodyRef,
    transform: "scale(1)",
    from: { transform: "scale(0)" },
  });

  const footerAnimation = useSpring({
    ref: footerRef,
    transform: "scale(1)",
    from: { transform: "scale(0)" },
  });

  useChain(
    [
      { current: headerRef.current },
      { current: bodyRef.current },
      { current: footerRef.current },
    ],
    [0, 0.3, 0.6]
  );

  useEffect(() => {
    if (!quiz && id) {
      getQuiz();
    }
  }, [quiz, id, getQuiz]);

  if (quiz) {
    return (
      <div className="flex flex-col flex-1">
        <animated.div
          style={headerAnimation}
          className="flex-grow-0 flex-shrink"
        >
          <QuizHeader title={quiz.title} image={quiz.image_url} />
        </animated.div>
        <animated.div
          style={bodyAnimation}
          className="flex-grow flex-shrink py-8"
        >
          <QuizBody answer={answer} handleAnswerChange={handleAnswerChange} />
        </animated.div>
        <animated.div
          style={footerAnimation}
          className="sticky bottom-0 md:block flex-grow-0 flex-shrink"
        >
          <QuizFooter
            loading={answerLoading || endLoading}
            next={nextStep()}
            back={previousStep()}
          />
        </animated.div>
      </div>
    );
  }
  return (
    <PageLoader loading={quizLoading} error={quizError} onRefresh={getQuiz}>
      <div></div>
    </PageLoader>
  );
};

export default QuizManager;
